/* eslint-disable react/no-array-index-key */
import React, { memo, useState } from "react";
import { Backdrop, Divider, Grow, Icon, List, ListItem, Popper, styled } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import borders from "assets/theme/base/borders";
import { createPortal } from "react-dom";

const { borderRadius } = borders;

// Component to render different content types
const ContentRenderer = memo(({ content, nestedLevel = 0 }) => {
  switch (content.type) {
    case "text":
      return (
        <MDTypography sx={content.style || {}} variant={content.variant || "body2"}>
          {content.value}
        </MDTypography>
      );

    case "icon":
      return <Icon sx={content.style || {}}>{content.value}</Icon>;

    case "divider":
      return <Divider sx={content.style || {}} />;

    case "nested":
      return (
        <MDBox sx={content.style || {}}>
          {(content?.value || []).map((children, index) => (
            <ContentRenderer
              key={`${nestedLevel}-${index}`}
              nestedLevel={nestedLevel + 1}
              content={children}
            />
          ))}
        </MDBox>
      );

    case "list":
      return (
        <List sx={{ listStyleType: "disc", pl: 2, ...(content.style || {}) }}>
          {(content?.value || []).map((children, subIndex) => (
            <ListItem
              disablePadding
              sx={children.type !== "list" ? { display: "list-item" } : {}}
              key={subIndex}
            >
              <ContentRenderer
                key={`${nestedLevel}-${subIndex}`}
                nestedLevel={nestedLevel + 1}
                content={children}
              />
            </ListItem>
          ))}
        </List>
      );

    default:
      return null;
  }
});

const TooltipContent = memo(({ data }) => {
  if (!data.length) return null;

  return data.map((contentBlock, index) => <ContentRenderer key={index} content={contentBlock} />);
});

const StyledPopper = styled(Popper)(({ theme }) => ({
  '&[data-popper-placement*="bottom"] .arrow': {
    top: 0,
    left: 0,
    marginTop: "-0.9em",
    width: "3em",
    height: "1em",
    "&::before": {
      borderWidth: "0 1em 1em 1em",
      borderColor: `transparent transparent ${theme.palette.background.paper} transparent`,
    },
  },
  '&[data-popper-placement*="top"] .arrow': {
    bottom: 0,
    left: 0,
    marginBottom: "-0.9em",
    width: "3em",
    height: "1em",
    "&::before": {
      borderWidth: "1em 1em 0 1em",
      borderColor: `${theme.palette.background.paper} transparent transparent transparent`,
    },
  },
  '&[data-popper-placement*="right"] .arrow': {
    left: 0,
    marginLeft: "-0.9em",
    height: "3em",
    width: "1em",
    "&::before": {
      borderWidth: "1em 1em 1em 0",
      borderColor: `transparent ${theme.palette.background.paper} transparent transparent`,
    },
  },
  '&[data-popper-placement*="left"] .arrow': {
    right: 0,
    marginRight: "-0.9em",
    height: "3em",
    width: "1em",
    "&::before": {
      borderWidth: "1em 0 1em 1em",
      borderColor: `transparent transparent transparent ${theme.palette.background.paper}`,
    },
  },
}));

// z-index values in MUI
// mobile stepper: 1000
// fab: 1050
// speed dial: 1050
// app bar: 1100
// drawer: 1200
// modal: 1300
// snackbar: 1400
// tooltip: 1500

const PopperWithHtml = memo(
  ({
    children,
    data,
    className,
    backgroundColor,
    variant,
    title,
    attachBackdropToBody = false,
    ...props
  }) => {
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const canBeOpen = open && Boolean(anchorEl);
    const [arrowRef, setArrowRef] = useState(null);
    const id = canBeOpen ? "scroll-playground" : undefined;

    return (
      <>
        <MDBox
          ref={setAnchorEl}
          aria-describedby={id}
          onClick={() => setOpen((o) => !o)}
          sx={{ cursor: "pointer" }}
        >
          {children}
        </MDBox>
        {attachBackdropToBody ? (
          createPortal(
            <Backdrop sx={{ zIndex: 1400 }} open={open} onClick={() => setOpen(false)} />,
            document.body
          )
        ) : (
          <Backdrop sx={{ zIndex: 1400 }} open={open} onClick={() => setOpen(false)} />
        )}
        <StyledPopper
          open={open}
          anchorEl={anchorEl}
          id={id}
          sx={{ zIndex: 1500 }}
          disablePortal={false}
          transition
          modifiers={[
            {
              name: "arrow",
              enabled: true,
              options: {
                element: arrowRef,
              },
            },
          ]}
          {...props}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps}>
              <MDBox
                sx={{
                  p: 1,
                  textAlign: "start",
                  bgcolor: "background.paper",
                  borderRadius: borderRadius.lg,
                }}
              >
                <MDBox
                  component="span"
                  className="arrow"
                  ref={setArrowRef}
                  sx={{
                    position: "absolute",
                    fontSize: 7,
                    width: "3em",
                    height: "3em",
                    "&::before": {
                      content: '""',
                      margin: "auto",
                      display: "block",
                      width: 0,
                      height: 0,
                      borderStyle: "solid",
                    },
                  }}
                />
                <MDBox
                  sx={{
                    p: 2,
                    maxHeight: "50vh",
                    overflowY: "auto",
                  }}
                >
                  <TooltipContent data={data} />
                </MDBox>
              </MDBox>
            </Grow>
          )}
        </StyledPopper>
      </>
    );
  }
);

export default PopperWithHtml;
